<template>
  <div class="input-group spacer hcl-favorites">
    <Multiselect
      v-model="selectionId"
      label="text"
      valueProp="id"
      :options="selectionOptions"
      :searchable=true
      :canClear=false
      :canDeselect=false
      noOptionsText="--"
    >
    </Multiselect>
    <div
      v-if="selectionOptionsCount > 0"
      class="input-group-append"
    >
      <button
        type="button"
        class="button-icon btn btn-outline-default"
        @click="toggleModalFavorites"
      >
        <i
          v-if="modeModalFavorites === 'add'"
          class="eii--fa6-regular--star"
        ></i>
        <i
          v-if="modeModalFavorites === 'edit'"
          class="eii--fa6-solid--star"
        ></i>
      </button>
    </div>
    <hcl-modal-favorites
      v-if="modeModalFavorites === 'add'"
      :open="showModalFavorites"
      :title="props.messageModalTitleAdd"
      :buttonCancelText="props.messageModalButtonsCancel"
      :buttonSaveText="props.messageModalButtonsSave"
      v-on:modal-favorites-choice="onModalFavoritesChoice"
    >
    </hcl-modal-favorites>
    <hcl-modal-favorites
      v-if="modeModalFavorites === 'edit'"
      :open="showModalFavorites"
      :title="props.messageModalTitleEdit"
      :buttonDeleteText="props.messageModalButtonsDelete"
      :buttonCancelText="props.messageModalButtonsCancel"
      :buttonSaveText="props.messageModalButtonsSave"
      :favoritName="selectionText"
      v-on:modal-favorites-choice="onModalFavoritesChoice"
    >
    </hcl-modal-favorites>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'

import Multiselect from '@vueform/multiselect'

import HclModalFavorites from '@/components/ModalFavorites/ModalFavorites.vue'

import { useDataStore } from '@/stores/data'

import { useGlobalEventBus } from '@/composables/global-event-bus'

import { serialize } from '../Form/serialize'

interface FavoriteOption {
  id: number,
  text: string
}

interface Props {
  /**
   * Toolbar Content Action ID
   */
  id: string
  /**
   * Favorites Type
   */
  favoriteType: string
  /**
   * Favorite ID selected
   */
  favoriteId?: number
  /**
   * Favorites Options
   */
  favoriteOptions?: FavoriteOption[]
  /**
   * Favorite ID, No Selection
   */
  favoriteIdNoSelection: number
  /**
   * ID of the form to which the Favorites belong
   */
  favoriteFormId: string
  /**
   * URL to load Favorite
   */
  urlsLoad: string
  /**
   * URL to save Favorite
   */
  urlsSave: string
  /**
   * URL to delete Favorite
   */
  urlsDelete: string
  /**
   * URL to redirect to after Delete or Save, set if favorites in searchlist
   */
  urlsRedirect?: string
  /**
   * Modal Title Add
   */
  messageModalTitleAdd: string
  /**
   * Modal Title Edit
   */
  messageModalTitleEdit: string
  /**
   * Modal Button Delete
   */
  messageModalButtonsDelete: string
  /**
   * Modal Button Cancel
   */
  messageModalButtonsCancel: string
  /**
   * Modal Button Save
   */
  messageModalButtonsSave: string
}

const props = defineProps<Props>()

const { link } = useDataStore()

const { eventBus } = useGlobalEventBus()

const bucket = link(props.favoriteFormId)

const getFavoriteOptionById = (id: number) => {
  return selectionOptions.value.filter((option) => {
    return option.id === id
  })
}

const selectionId = ref(props.favoriteId ?? -1)
const selectionText = ref('')
const selectionOptions = ref(props.favoriteOptions ?? [])

const suppressFavoriteLoading = ref(false)

/*
 * Form Handling
 */
const resetSearchFormValues = () => {

  for (const field of bucket.value.fields) {
    if (typeof field.value === 'object') {
      field.value = []
    } else {
      field.value = ''
    }
  }

}

const addFreeFormHtmlInputValues = (data: any, fieldsFormName: string) => {
  /*
    * All form fields with type 'html' are assumed to be free form,
    * i.e. they are not rendered via vue, and thus we do not know
    * which values have been entered in the input fields.
    * We need to use normal javascript to retrieve the form
    * and the associated input fields.
    */
  const formSelector = 'form[id="' + fieldsFormName + '"]'
  const form = document.querySelector(formSelector)

  if (form === null) return

  /*
    * Sadly using `new FormData(form)` does not work, so we use
    * a alternative serialization library, which only returns
    * the input fields from form fields with type 'html'.
    */
  const formDataHtml = serialize(form as HTMLFormElement, { hash: true })

  const dataToAdd = formDataHtml['searchField']

  for (const key in dataToAdd) {
    if (!(key in data)) {
      data[key] = dataToAdd[key]
    }
  }
}

const retrieveSearchFormValues = () => {

  const result = {}

  for (const field of bucket.value.fields) {

    if (typeof field.value === 'object' && field.value !== null && field.value.length > 0) {
      if (field.group && field.group !== '') {
        if (!(field.group in result)) result[field.group] = {}
        result[field.group][field.name] = field.value
      } else {
        result[field.name] = field.value
      }
    }

    if (typeof field.value === 'string' && field.value.length > 0) {
      if (field.group && field.group !== '') {
        if (!(field.group in result)) result[field.group] = {}
        result[field.group][field.name] = field.value
      } else {
        result[field.name] = field.value
      }
    }
  }

  return result

}

/*
 * Input / Output Handling
 */
const loadFavorite = async (id: number) => {

  const url = props.urlsLoad + '/' + id

  const response = await fetch(
    url
  )

  const data = await response.json()

  if ('state' in data && data.state === 'success' && 'text' in data && 'content' in data && 'searchField' in data.content) {

    const fieldsUpdated = data.content['searchField']

    for (const field of bucket.value.fields) {
      if (Object.keys(fieldsUpdated).includes(field.name)) {
        field.value = fieldsUpdated[field.name]
      }
    }

    const option = getFavoriteOptionById(id)

    if (!option) return

    option[0].text = data.text
    selectionText.value = data.text

  }

}

const saveFavorite = async (name: string) => {

  const fieldsValues = retrieveSearchFormValues()

  addFreeFormHtmlInputValues(fieldsValues, 'people_finder')

  const body = {
    type: props.favoriteType,
    id: selectionId.value,
    text: name,
    content: fieldsValues
  }

  const url = props.urlsSave

  const response = await fetch(
    url,
    {
      method: 'POST',
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body)
    }
  )

  const data = await response.json()

  if ('state' in data && data.state === 'success' && 'id' in data) {

    if (props.urlsRedirect) {

      window.location.assign(props.urlsRedirect + '?favoriteId=' + data.id)

    } else {

      const option = {
        id: data.id,
        text: name
      } as FavoriteOption

      selectionOptions.value.push(option)

      suppressFavoriteLoading.value = true

      selectionId.value = data.id
      selectionText.value = name

    }
  }

}

const deleteFavorite = async (id: number) => {

  const url = props.urlsDelete + '/' + id

  const response = await fetch(
    url,
    {
      method: 'POST',
      headers: {
        "Content-Type": "application/json"
      }
    }
  )

  const data = await response.json()

  if ('state' in data && data.state === 'success') {

    if (props.urlsRedirect) {

      window.location.assign(props.urlsRedirect)

    } else {

      const index = selectionOptions.value.findIndex((element) => { return element.id === id })

      selectionOptions.value.splice(index, 1)

      selectionId.value = props.favoriteIdNoSelection

      const option = getFavoriteOptionById(selectionId.value)

      if (!option) return

      selectionText.value = option[0].text

    }

  }

}

const switchFavorite = (idNew: number, idOld: number) => {

  if (props.urlsRedirect) {

    if (idNew !== idOld && idNew > 0) {
      window.location.assign(props.urlsRedirect + '?favoriteId=' + idNew)
    } else {
      window.location.assign(props.urlsRedirect)
    }

  } else {

    if (suppressFavoriteLoading.value) {
      suppressFavoriteLoading.value = !suppressFavoriteLoading.value
      return
    }

    resetSearchFormValues()

    if (idNew !== idOld && idNew !== props.favoriteIdNoSelection) {
      loadFavorite(idNew)
    }

    eventBus.emit({
      sourceType: 'favorites',
      sourceId: props.id,
      eventType: 'selection-change',
      eventValue: idNew
    })

  }

}

/*
 * Modal Handling
 */
const showModalFavorites = ref(false)

const toggleModalFavorites = () => {
  showModalFavorites.value = !showModalFavorites.value
}

const modeModalFavorites = computed(() => {
  return selectionId.value > 0 ? 'edit' : 'add'
})

const onModalFavoritesChoice = (event) => {

  showModalFavorites.value = !showModalFavorites.value

  if (event.choice === 'delete') {
    deleteFavorite(selectionId.value)
  }

  if (event.choice === 'save') {
    saveFavorite(event.value)
  }

}

/*
 * Initialization
 */
const selectionOptionsCount = computed(() => {
  return selectionOptions.value ? selectionOptions.value.length : 0
})

if (selectionOptionsCount.value > 0) {
  selectionText.value = getFavoriteOptionById(selectionId.value)[0].text
}

if (selectionId.value > 0) {
  loadFavorite(selectionId.value)
}

watch(
  selectionId,
  (n, o) => {

    switchFavorite(n, o)

  }
)
</script>

<style lang="scss" scoped>
.hcl-favorites {

  .multiselect {
    min-width: 8rem;
    max-width: 15rem;
    height: 36.5px;
    --ms-max-height: 20rem;

    @media screen and (min-width: 768px) {
      width: 15rem;
    }
  }

  .button-icon {
    background-color: #e9ecef;
  }

}
</style>
